import { AnimatePresence, motion } from "framer-motion";
import { Flame, Package, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { artAssets, officialTcgCards } from "../data/site";
import { awardFeat, getLocalPoints } from "../utils/rewards";
import {
  cardById,
  fetchCloudCollection,
  getLocalCollection,
  openPack,
  PACK_COST,
  SHRED_COPIES,
  shredCard,
  shredValueFor,
  WEEKLY_COST,
  setProgressOf,
  variantOf,
  weeklyDropAvailable,
} from "../utils/tcg";

/* Pack crate — spend PTS, crack three cards face-down, flip them one by one.
   One half-price weekly supply drop keeps every runner's collection growing.
   Duplicate piles of three melt back into PTS so no pull is ever wasted. */
export function TcgPacks() {
  const [balance, setBalance] = useState(getLocalPoints());
  const [pack, setPack] = useState(null);
  const [revealed, setRevealed] = useState(0);
  const [opening, setOpening] = useState(false);
  const [celebration, setCelebration] = useState(false);
  const [collection, setCollection] = useState(getLocalCollection());
  const [shredding, setShredding] = useState(null);
  const [weeklyUp, setWeeklyUp] = useState(() => weeklyDropAvailable(null));

  const price = weeklyUp ? WEEKLY_COST : PACK_COST;

  useEffect(() => {
    const onRewards = (event) => setBalance(event.detail.total);
    window.addEventListener("dolo-rewards", onRewards);
    /* when a tag is linked, the vault's collection is the record of truth */
    fetchCloudCollection().then((cloud) => {
      if (cloud?.cards) setCollection(cloud.cards);
      setWeeklyUp(weeklyDropAvailable(cloud?.weekly_week ?? null));
    });
    return () => window.removeEventListener("dolo-rewards", onRewards);
  }, []);

  const ownedCount = officialTcgCards.filter((card) => (collection[card.id] || 0) > 0).length;

  const crackPack = async () => {
    if (opening) return;
    setOpening(true);
    setPack(null);
    setRevealed(0);
    setCelebration(false);
    try {
      const { cards: ids } = await openPack(weeklyUp);
      if (weeklyUp) setWeeklyUp(false);
      const cards = ids.map(cardById).filter(Boolean);
      setPack(cards);
      setCollection(getLocalCollection());
      cards.forEach((card, index) => {
        setTimeout(() => {
          setRevealed(index + 1);
          if (card.rarity === "legendary" || card.rarity === "epic") {
            setCelebration(true);
            awardFeat(card.rarity === "legendary" ? "LEGENDARY PULL" : "EPIC PULL");
            setTimeout(() => setCelebration(false), 2600);
          }
        }, 900 * (index + 1));
      });
    } catch (error) {
      toast.error(error.message || "CRATE JAMMED — RETRY.");
    }
    setOpening(false);
  };

  const meltCard = async (card) => {
    if (shredding) return;
    setShredding(card.id);
    try {
      const data = await shredCard(card.id);
      toast.success(`+${data.refund} PTS — ${card.name.split(",")[0]} MELTED TO SCRAP.`);
      setCollection(getLocalCollection());
    } catch (error) {
      toast.error(error.message || "SHRED REJECTED.");
    }
    setShredding(null);
  };

  return (
    <div className="pack-crate" data-testid="pack-crate">
      <div className="pack-crate-head">
        <h3>SUPPLY CRATE</h3>
        <p>
          Three cards per crate, sealed under the official Pandarmor back. Commons hold the line, rares punch
          up, legendaries break games — 50 / 35 / 15 odds per slot. Three duplicate copies of any unit melt
          back into PTS, so no pull is ever wasted.
        </p>
        <div className="pack-balance-row">
          <span className="pack-balance" data-testid="pack-balance">YOUR STASH — {balance} PTS</span>
          <button
            type="button"
            className="pack-open-button"
            onClick={crackPack}
            disabled={opening || balance < price}
            data-testid="pack-open-button"
          >
            <Package size={15} /> {opening ? "CRACKING…" : `CRACK CRATE — ${price} PTS`}
          </button>
          {weeklyUp && (
            <span className="weekly-badge" data-testid="weekly-drop-badge">
              WEEKLY SUPPLY DROP — 50% OFF THIS WEEK
            </span>
          )}
        </div>
      </div>

      {pack && (
        <div className="pack-row" data-testid="pack-row">
          {pack.map((card, index) => (
            <div key={`${card.id}-${index}`} className={`pack-card flip-card${revealed > index ? " is-revealed" : ""}`} data-testid={`pack-card-${index}`}>
              <span className={`flip-face flip-front holo-card rarity-${card.rarity}`}>
                <img src={card.image} alt={`${card.name} pulled card`} />
                <span className="pack-card-name">{card.name.split(",")[0]}</span>
                {variantOf(card) && <span className="variant-chip pack-card-variant" data-testid={`pack-variant-${index}`}>{variantOf(card)} · {card.cardNo}</span>}
              </span>
              <span className="flip-face flip-back">
                <img src={artAssets.cardBack} alt="" aria-hidden="true" />
              </span>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {celebration && (
          <motion.div
            className="pack-celebration"
            data-testid="pack-celebration"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Sparkles size={34} />
            <h3>LEGENDARY PULL</h3>
            <p>The Pandarmor seal cracks. The whole wasteland hears it.</p>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="collection-block">
        <h4 data-testid="collection-title">
          MY COLLECTION <span>{ownedCount} / {officialTcgCards.length} UNITS</span>
        </h4>
        <div className="collection-grid" data-testid="collection-grid">
          {officialTcgCards.map((card) => {
            const count = collection[card.id] || 0;
            const setProgress = setProgressOf(card, collection);
            return (
              <div key={card.id} className={`collection-card-item rarity-${card.rarity}${count === 0 ? " is-unpulled" : ""}`} data-testid={`collection-card-${card.id}`}>
                <img loading="lazy" src={count > 0 ? card.image : artAssets.cardBack} alt={count > 0 ? `${card.name} owned card` : "Unpulled card back"} />
                <span className="collection-item-name">{count > 0 ? card.name.split(",")[0] : "UNPULLED"}</span>
                {count > 0 && variantOf(card) && <span className="variant-chip collection-item-variant" data-testid={`collection-variant-${card.id}`}>{variantOf(card)} · {card.cardNo}</span>}
                {setProgress && (
                  <span className={`variant-chip set-progress-chip${setProgress.owned === setProgress.total ? " is-complete" : ""}`} data-testid={`set-progress-${card.id}`}>
                    SET {setProgress.owned}/{setProgress.total}
                  </span>
                )}
                {count > 0 && <span className="count-chip" data-testid={`collection-count-${card.id}`}>×{count}</span>}
                {count >= SHRED_COPIES && (
                  <button
                    type="button"
                    className="shred-button"
                    onClick={() => meltCard(card)}
                    disabled={shredding === card.id}
                    data-testid={`shred-button-${card.id}`}
                    aria-label={`Melt 3 ${card.name} copies for ${shredValueFor(card.id)} points`}
                  >
                    <Flame size={10} /> {shredding === card.id ? "MELTING…" : `MELT ×${SHRED_COPIES} +${shredValueFor(card.id)} PTS`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
